import { toPlainText } from '@portabletext/react';
import Layout from '@/components/layout';
import Debug from '@/components/debug';
import { FagmoblerPortableText } from '@/components/portableText';
import { ArticleJsonLd, NextSeo } from 'next-seo';
import clsx from 'clsx';
import { format } from 'date-fns';
import { nb } from 'date-fns/locale';
import { MaterializedArticlePage } from '@fagmobler/sanity';
import { ProductCategories } from '@/components/productCategories';

export type ArticlePageProps = {
  data: {
    transformedSanityArticlePage: MaterializedArticlePage;
    mainImageUrl?: string | null;
    canonical: string;
  };
  isFallback?: boolean;
  preview: boolean;
  previewData?: {
    id: string;
    apiReadToken: string;
  };
};

export function ArticlePage({ data, preview, isFallback }: ArticlePageProps) {
  const { mainImageUrl, canonical, transformedSanityArticlePage } = data;
  const { title, lead, body, author, categories, _createdAt, _updatedAt } =
    transformedSanityArticlePage;

  let imageUrl = mainImageUrl;

  !mainImageUrl &&
    body &&
    body?.forEach((element) => {
      if (element && 'croppedImageUrl' in element) {
        imageUrl = element.croppedImageUrl;
      }
    });

  return (
    <Layout loading={isFallback} isPreview={preview}>
      <NextSeo
        title={title}
        description={lead ? toPlainText(lead) : ''}
        canonical={canonical}
        openGraph={
          imageUrl
            ? {
                images: [
                  {
                    url: imageUrl,
                  },
                ],
              }
            : undefined
        }
      />
      {/* Disable until we have authorName that is REQUIRED */}
      <ArticleJsonLd
        url={canonical}
        title={title || ''}
        images={imageUrl ? [imageUrl] : []}
        datePublished={_createdAt}
        dateModified={_updatedAt}
        publisherLogo="https://www.fagmobler.no/gfx/logo_black.jpg"
        publisherName="Fagmøbler"
        description={(lead && toPlainText(lead)) || ''}
        isAccessibleForFree={true}
        authorName={[
          author && {
            type: 'person',
            name: author?.name,
            url: author?.url,
          },
          {
            type: 'organization',
            name: 'Fagmøbler',
            url: 'https://fagmobler.no',
          },
        ].filter(Boolean)}
      />
      <div className={clsx('max-w-4xl mx-auto space-y-4')}>
        <h1 className="hd-xl md:hd-3xl mt-10 mb-2 break-words">{title}</h1>
        {lead && <FagmoblerPortableText value={lead} />}

        {body && <FagmoblerPortableText value={body} />}
        <div className="cap-xs justify-between py-3 text-neutral-70 leading-loose hidden">
          <div>Skrevet av {author?.name || 'Fagmbøler'}</div>

          <div>
            Først publisert{' '}
            {format(new Date(_createdAt), 'd. MMMM yyyy', { locale: nb })}
          </div>
          <div>
            Sist oppdatert{' '}
            {format(new Date(_updatedAt), 'd. MMMM yyyy', { locale: nb })}
          </div>
        </div>
        {categories && categories.length > 0 && (
          <>
            <h2 className="hd-lg md:hd-xl mt-10 mb-2 break-words">
              Kategorier
            </h2>
            <ProductCategories categories={categories} />
          </>
        )}
      </div>

      <Debug data={data} label="Article" />
    </Layout>
  );
}

export default ArticlePage;
